
import { CompanyRequest } from "@/lib/types";
import { useToast } from "@/hooks/use-toast";
import { useCompanyStore, useCompanyRequestStore } from "@/lib/store";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Inbox } from "lucide-react";
import PendingRequestsTable from "./PendingRequestsTable";
import ProcessedRequestsTable from "./ProcessedRequestsTable";
import { v4 as uuidv4 } from 'uuid';

interface CompanyRequestTablesProps {
  pendingRequests: CompanyRequest[];
  processedRequests: CompanyRequest[];
}

const CompanyRequestTables = ({ pendingRequests, processedRequests }: CompanyRequestTablesProps) => {
  const { toast } = useToast();
  const { updateRequestStatus } = useCompanyRequestStore();
  const { addCompany } = useCompanyStore();

  const handleApprove = (request: CompanyRequest) => {
    updateRequestStatus(request.id, 'approved');
    
    // Add the company
    addCompany({
      name: request.companyName,
      locations: [request.location],
      bio: request.additionalInfo || '',
    });
    
    toast({
      title: "Company approved",
      description: `${request.companyName} has been added to the database`,
    });
  };

  const handleReject = (request: CompanyRequest) => {
    updateRequestStatus(request.id, 'rejected');
    toast({
      title: "Request rejected",
      description: `The request for ${request.companyName} has been rejected`,
    });
  };

  // If there are no requests at all (both pending and processed)
  if (pendingRequests.length === 0 && processedRequests.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Company Requests</CardTitle>
          <CardDescription>
            There are no company requests available at this time.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-redflags-gray">
            <Inbox className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-lg mb-2">No requests yet</p>
            <p className="text-sm max-w-md mx-auto">
              Company requests will appear here when users submit them through the "Request Company" form.
              You can also add companies directly from the Companies tab.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <PendingRequestsTable 
        pendingRequests={pendingRequests}
        onApprove={handleApprove}
        onReject={handleReject}
      />
      
      {processedRequests.length > 0 && (
        <ProcessedRequestsTable processedRequests={processedRequests} />
      )}
    </div>
  );
};

export default CompanyRequestTables;
